import React from "react";
import NotificationBell from "@/components/NotificationBell";
import { Briefcase, ChevronDown, LogOut, User, ClipboardList } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * SuiteTopBar — Top Bar comune a tutti i moduli della Gruppo Impresa Suite.
 *
 * v1.11.0 · UX Evolution v3 · Fase A · Iterazione 3 (26/07/2026)
 * ==============================================================
 * Fascia FULL-WIDTH (bordo bottom edge-to-edge) con contenuto interno
 * allineato al container principale `max-w-[1600px] mx-auto`.
 * Effetto professionale ala Microsoft 365 su ultrawide.
 */
export function SuiteTopBar({
  suite,
  myWorkCount = 0,
  onOpenMyWork,
  onLogout,
  onOpenProfile,
  className = "",
}) {
  const { studio, operator } = suite || {};

  return (
    <header
      className={`suite-topbar w-full ${className}`}
      style={{
        minHeight: "var(--suite-topbar-h)",
        maxHeight: "var(--suite-topbar-h)",
        backgroundColor: "var(--suite-surface)",
        borderBottom: "1px solid var(--suite-border)",
      }}
      data-testid="suite-topbar"
    >
      <div
        className="flex items-center gap-4 w-full h-full"
        style={{
          paddingLeft: 16,
          paddingRight: 16,
          /* v1.1 (26/07/2026): +6px paddingTop interno per abbassare
           * di 3-6px logo/testi/icone/badge — la TopBar cresce di 6px
           * complessivi (58px totali) ma il gestionale sotto NON si
           * sposta verso il basso perché il padding esterno superiore
           * dello shell è a 0. */
          paddingTop: "6px",
        }}
      >
      {/* ── LOGO + WORDMARK ─────────────────────────────── */}
      <div className="flex items-center gap-2.5 shrink-0" style={{ width: 224 }} data-testid="suite-topbar-brand">
        <span
          className="flex items-center justify-center rounded-lg"
          style={{
            width: 40,
            height: 40,
            backgroundColor: "var(--gis-green)",
            color: "#FFFFFF",
          }}
          aria-hidden="true"
        >
          <Briefcase className="h-5 w-5" />
        </span>
        <div className="hidden md:flex flex-col leading-tight">
          <span
            className="gis-heading font-semibold whitespace-nowrap"
            style={{
              fontSize: 12,
              color: "var(--gis-green)",
              letterSpacing: "0.025em",
            }}
          >
            GRUPPO IMPRESA SUITE
          </span>
          <span
            className="gis-heading-wide"
            style={{
              fontSize: 10.5,
              color: "var(--suite-ink-500)",
              letterSpacing: "0",
              marginTop: 1,
            }}
          >
            Gestionale di Studio
          </span>
        </div>
      </div>

      <div
        className="hidden md:block shrink-0"
        style={{ width: 1, height: 24, backgroundColor: "var(--suite-divider)" }}
        aria-hidden="true"
      />

      {/* ── STUDIO NAME ─────────────────────────────────── */}
      <div
        className="min-w-0 flex-1 flex flex-col justify-center leading-tight"
        title={studio?.tooltip || studio?.name}
        data-testid="suite-topbar-studio"
      >
        <span
          className="gis-heading font-semibold truncate"
          style={{
            fontSize: 13,
            color: "var(--suite-ink-900)",
            letterSpacing: "-0.005em",
          }}
        >
          {studio?.name || "—"}
        </span>
        {studio?.subtitle && (
          <span
            className="truncate"
            style={{
              fontSize: 10.5,
              color: "var(--suite-ink-500)",
              marginTop: 1,
            }}
          >
            {studio.subtitle}
          </span>
        )}
      </div>

      {/* ── AZIONI DI SERVIZIO ───────────────────────────── */}
      <div className="flex items-center gap-1 shrink-0">
        <NotificationBell />

        {typeof onOpenMyWork === "function" && (
          <button
            type="button"
            onClick={onOpenMyWork}
            className="relative flex items-center justify-center rounded-md transition-colors hover:bg-[var(--suite-surface-hover)]"
            style={{ width: 36, height: 36, color: "var(--suite-ink-700)" }}
            title={`Il mio lavoro${myWorkCount ? ` (${myWorkCount})` : ""}`}
            data-testid="suite-topbar-my-work"
          >
            <ClipboardList className="h-[18px] w-[18px]" />
            {myWorkCount > 0 && (
              <span
                className="absolute flex items-center justify-center rounded-full font-bold text-white"
                style={{
                  top: 4,
                  right: 4,
                  minWidth: 15,
                  height: 15,
                  padding: "0 3px",
                  fontSize: 9,
                  lineHeight: 1,
                  backgroundColor: "var(--gis-green)",
                  fontFamily: "var(--gis-font-heading)",
                }}
              >
                {myWorkCount > 99 ? "99+" : myWorkCount}
              </span>
            )}
          </button>
        )}

        {operator && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1.5 rounded-md transition-colors hover:bg-[var(--suite-surface-hover)]"
                style={{
                  padding: "4px 8px 4px 4px",
                  color: "var(--suite-ink-700)",
                }}
                data-testid="suite-topbar-user-menu"
                title={`${operator.name} · ${operator.role}`}
              >
                <span
                  className="flex items-center justify-center rounded-full font-semibold text-white"
                  style={{
                    width: 26,
                    height: 26,
                    fontSize: 10,
                    backgroundColor: "var(--gis-green)",
                    fontFamily: "var(--gis-font-heading)",
                  }}
                >
                  {operator.initials}
                </span>
                <span
                  className="hidden lg:inline gis-heading font-semibold"
                  style={{ fontSize: 12 }}
                >
                  {operator.name.split(" ")[0]}
                </span>
                <ChevronDown className="h-3 w-3 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[220px]">
              <DropdownMenuLabel>
                <div className="flex flex-col leading-tight">
                  <span className="font-semibold" style={{ fontSize: 13 }}>
                    {operator.name}
                  </span>
                  <span
                    className="font-normal"
                    style={{ fontSize: 11, color: "var(--suite-ink-500)" }}
                  >
                    {operator.email || operator.role}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {onOpenProfile && (
                <DropdownMenuItem
                  onSelect={onOpenProfile}
                  className="cursor-pointer"
                  data-testid="suite-topbar-profile"
                >
                  <User className="mr-2 h-4 w-4" />
                  Profilo
                </DropdownMenuItem>
              )}
              {onLogout && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={onLogout}
                    className="cursor-pointer text-red-600 focus:text-red-700"
                    data-testid="suite-topbar-logout"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Esci
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      </div>
    </header>
  );
}

export default SuiteTopBar;
